import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import EnableProfileModal from '@components/EnableProfileModal';
import CheckBoxField from '@components/Form/CheckBoxField';
import MultiValueAutocompleteField from '@components/Form/MultiValueAutocompleteField';
import TextAreaField from '@components/Form/TextAreaField';
import TextField from '@components/Form/TextField';
import {
  useCurrentUser,
  useForumPostAdminUpdate,
  useForumPostCreate,
  useForumPostUpdate,
} from '@hooks';
import useEntitiesTop from '@hooks/useEntitiesTop';
import { forum_post_path } from '@routes';
import { MATCH_EXACT, autocompleteEntities, createEntity } from '@utils/backend-api/entities';
import {
  FORUM_POSTS_KEY,
  ForumPost,
  NewForumPost as NewForumPostType,
} from '@utils/backend-api/forumPosts';
import { stripHtml } from '@utils/formatting';
import { appendSchemeToUrlString, optionalSchemeURLRegex } from '@utils/url_validations';

import AnonymousPostBlock from './AnonymousPostBlock';
import styles from './styles.module.scss';

const validationSchema = () =>
  Yup.object({
    title: Yup.string().required('A title is required'),
    body: Yup.string().required('A post is required'),
    external_url: Yup.string().matches(optionalSchemeURLRegex, 'The url must be valid'),
  });

export const QUICK_TAG_JOBS = 'jobs';
export const QUICK_TAG_FUNDING = 'funding';
export const QUICK_TAG_LAUNCH = 'launch';

export default function NewForumPostForm({
  initialValues,
  onCreateSuccess,
  isManaging,
}: {
  initialValues: NewForumPostType;
  onCreateSuccess: (forumPost: ForumPost) => void;
  isManaging;
}) {
  const { currentUser } = useCurrentUser();
  const firstName = currentUser?.name?.split(' ')[0] || 'You';
  const isEditing = !!initialValues.id;
  const { mutateAsync: updatePostAsync } = useForumPostUpdate();
  const { mutateAsync: updateAdminPostAsync } = useForumPostAdminUpdate();
  const { mutateAsync: createPostAsync } = useForumPostCreate();
  const { data: topEntityOptions, isLoading: isLoadingTopEntityOptions } = useEntitiesTop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canComment = currentUser?.permitted_to_comment && currentUser?.can_actually_comment;
  const [canInitiallyComment] = useState(canComment);
  const buttonText = isEditing ? 'Update' : `Post as ${firstName}`;

  initialValues.body = stripHtml(initialValues.body);

  const onQuickTagClick = async (tagName, values, setFieldValue) => {
    const entities = await autocompleteEntities({
      fragment: tagName,
      relationship_sources: ['chat_gpt', 'user_action'],
      order_entities_by: ['is_important', 'pdl_verified'],
      match_type: MATCH_EXACT,
    });
    if (entities.length === 0) return; // this should never happen
    if (values.entity_tags.map((entity) => entity.value).includes(entities[0].value)) {
      setFieldValue(
        'entity_tags',
        values.entity_tags.filter((entity) => entity.value !== entities[0].value)
      );
    } else {
      setFieldValue('entity_tags', [...values.entity_tags, entities[0]]);
    }
  };

  const handleSubmit = async (values) => {
    const data: ForumPost = {
      ...values,
      external_url: appendSchemeToUrlString(values.external_url),
      entity_tags: values?.entity_tags.map((entity) => entity.value),
    };

    if (isEditing) {
      const updateAction = isManaging ? updateAdminPostAsync : updatePostAsync;
      return updateAction(data, {
        onSuccess: (data: ForumPost) => {
          toast.success('Post has been updated!');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_post_path(data.id));
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not updated, please try again.');
        },
      });
    }

    return createPostAsync(data, {
      onSuccess: (data: ForumPost) => {
        queryClient.invalidateQueries(FORUM_POSTS_KEY);
        onCreateSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        toast.error('Post was not created, please try again.');
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({ isSubmitting, submitForm, setFieldValue, values }) => (
        <Form className="form forum-post-form">
          <TextField className="mb-24" name="title" label="Title" placeholder="Type Title" />
          <TextAreaField
            name="body"
            className="mb-24"
            label="Post"
            autoComplete="off"
            placeholder="Type Text"
            rows={6}
          />
          {!isManaging && (
            <TextField
              className="mb-24"
              name="external_url"
              placeholder="Type URL (Optional)"
              label="Link"
            />
          )}
          <div className="TextField form-group">
            <label className="input-label" htmlFor={'Tags'}>
              Tags
            </label>
            <div className="w-full">
              <CheckBoxField
                name="job_tag"
                label="Job Posting (#jobs)"
                className={styles.firstCheckboxGroup}
                onClick={() => onQuickTagClick(QUICK_TAG_JOBS, values, setFieldValue)}
              />
              <CheckBoxField
                name="funding_tag"
                label="Funding Announcement (#funding)"
                onClick={() => onQuickTagClick(QUICK_TAG_FUNDING, values, setFieldValue)}
              />
              <CheckBoxField
                name="product_tag"
                label="Product Launch (#launch)"
                onClick={() => onQuickTagClick(QUICK_TAG_LAUNCH, values, setFieldValue)}
              />
              <MultiValueAutocompleteField
                placeholder="Type tags"
                name="entity_tags"
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    relationship_sources: ['chat_gpt', 'user_action'],
                    order_entities_by: ['is_important', 'pdl_verified'],
                  })
                }
                createMethod={(value) =>
                  createEntity({
                    name: value,
                    entity_type: 'other',
                    word_type: 'common',
                  })
                }
                defaultOptions={isLoadingTopEntityOptions ? [] : topEntityOptions}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <div className={styles.formActionsDigest}>
              {(!isEditing || isManaging) && (
                <CheckBoxField
                  name="email_digest_enabled"
                  label="Include in daily forum post email digest"
                  className="mt0"
                />
              )}
            </div>

            {canComment && (
              <div className="flex flex-col items-end">
                <PrimaryButton
                  as="button"
                  type="submit"
                  className="self-end"
                  disabled={isSubmitting}
                  fullWidth
                >
                  {values.is_anonymous ? 'Post anonymously' : buttonText}
                </PrimaryButton>
                {!isEditing && (
                  <AnonymousPostBlock isAnonymous={values.is_anonymous} justify="right" />
                )}
              </div>
            )}
            {!canInitiallyComment && (
              <div className={canComment ? '!hidden' : ''}>
                {!currentUser?.permitted_to_comment ? (
                  'Missing name / username, or not permitted to comment'
                ) : (
                  <EnableProfileModal
                    isLoggedIn={!!currentUser}
                    isSubscriber={currentUser?.is_subscriber}
                    isTriggeredByComment={true}
                    buttonClasses={clsx(styles.submitButton, 'button button-red button-small')}
                    commentButtonText={buttonText}
                    isCommentForForum={true}
                    handleSubmitComment={submitForm}
                  />
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
